import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';

import {
  getNotificationsListQueryKey,
  getNotificationsUnreadQueryKey,
  notificationsRead,
  notificationsReadAll,
  useNotificationsUnread,
} from '@/shared/api/generated/notifications/notifications';
import { unwrap } from '@/shared/api/unwrap';
import { useSession } from '@/shared/auth';
import { useChannel } from '@/shared/realtime';

/** Число непрочитанных в пространстве; 0 вне пространства. */
export function useUnreadCount(workspace: string): number {
  const query = useNotificationsUnread(workspace, {
    query: { select: unwrap, enabled: workspace !== '' },
  });

  return query.data?.unread ?? 0;
}

/**
 * Живые входящие: личный канал `user:{id}` сообщает о новом уведомлении —
 * счётчик и лента перечитываются. Заголовок вкладки получает «(N)», как в
 * почте: видно, даже когда вкладка не активна.
 */
export function useInboxLive(workspace: string): void {
  const queryClient = useQueryClient();
  const session = useSession();
  const me = session.status === 'authenticated' ? session.user.id : undefined;
  const unread = useUnreadCount(workspace);

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: getNotificationsUnreadQueryKey(workspace) });
    void queryClient.invalidateQueries({
      queryKey: getNotificationsListQueryKey(workspace),
      exact: false,
    });
  };

  useChannel(me === undefined ? undefined : `user:${me}`, invalidate);

  useEffect(() => {
    const base = 'Flow Do';

    document.title = unread > 0 ? `(${String(unread)}) ${base}` : base;

    return () => {
      document.title = base;
    };
  }, [unread]);
}

/** Действия над входящими с перечитыванием счётчика и ленты. */
export function useInboxActions(workspace: string) {
  const queryClient = useQueryClient();

  const refresh = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: getNotificationsUnreadQueryKey(workspace) }),
      queryClient.invalidateQueries({
        queryKey: getNotificationsListQueryKey(workspace),
        exact: false,
      }),
    ]);

  return {
    markRead: async (id: string) => {
      await notificationsRead(workspace, id);
      await refresh();
    },
    markAllRead: async () => {
      await notificationsReadAll(workspace);
      await refresh();
    },
  };
}
